import { Button } from '@/components/ui/Button';
import { transformSite } from '@/lib/helpers/site';
import { cn } from '@/lib/helpers/utils';
import { graphql, useStaticQuery } from 'gatsby';
import React from 'react';
import { connect } from 'react-redux';
import HeaderActions from './HeaderActions';
import Nav from './Nav';
import { siteLogoLink } from './utils/Logo';

export interface HeaderProps extends React.HTMLAttributes<HTMLElement> {
  isSignedIn: boolean;
}

/**
 * Renders out the header.
 *
 * @return {JSX}
 */
const Header = ({ isSignedIn, className }: HeaderProps) => {
  /** Get Static Data */
  const data = useStaticQuery(graphql`
    query {
      allMenuLinkContent(filter: { enabled: { eq: "1" } }, sort: [{ weight: ASC }, { title: ASC }]) {
        nodes {
          id
          weight
          enabled
          title
          menu_name
          external
          field_is_new
          link {
            uri
            title
          }
          parent {
            id
          }
        }
      }
      site {
        siteMetadata {
          title
          brandName
          logo
        }
      }
    }
  `);
  if (data?.site) data.site = transformSite(data.site);

  return (
    <header
      className={cn(
        `section-container-full block z-[2] isolate bg-white sticky top-0 border-b border-gray-200 [&:has(.header-extended)]:border-0`,
        className
      )}
    >
      <div className={`section-container-2xl h-[60px] lg:pb-0 lg:block`}>
        <div className={`flex justify-between items-center h-full w-full lg:justify-between lg:space-x-4`}>
          <div className="relative flex-1 order-1 lg:flex-none">
            {React.useMemo(
              () => siteLogoLink(data, 'block w-fit', 'h-[32px]', false, undefined, isSignedIn),
              [isSignedIn]
            )}
            <ul role="list" className="absolute z-[1] left-12 top-0">
              <li>
                <a
                  href="#main-content"
                  className="block overflow-hidden w-0 h-0 focus:w-auto focus:h-auto focus:overflow-visible"
                >
                  <Button appearance="primary" variant="outline" size="sm">
                    Skip to content
                  </Button>
                </a>
              </li>
            </ul>
          </div>
          <div className="order-3 pl-2 lg:order-2 lg:pl-0">
            <Nav staticData={data} />
          </div>
          <div className="order-2 lg:order-3 lg:min-w-[280px] lg:justify-end lg:flex">
            {React.useMemo(
              () => (
                <HeaderActions siteMetadata={data.site.siteMetadata} isSignedIn={isSignedIn} />
              ),
              [isSignedIn]
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

const mapStateToProps = ({ auth }: any) => {
  return {
    isSignedIn: auth.isSignedIn
  };
};

export default connect(mapStateToProps, null)(Header);
